/**
 * Dismissible — shared types and Zod prop schema.
 *
 * A container the member can dismiss: it renders its children until it is
 * dismissed, then renders nothing — and keeps rendering nothing for as long as
 * the host's dismissal store remembers `dismissKey`. With `close`, it draws its
 * own close (×) control in its top-right corner, so nothing it contains has to
 * know it is inside one. The dismissal is component-owned, so no tool name
 * reaches the document for it. Added after launch: every authored use declares
 * a fallback sibling (a `Stack` over the same children), so a build that
 * predates this kind still draws the content, undismissable (ADR-118 Rules 2-3).
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import type { IconName } from '../../elements/icons/registry';

export const DISMISSIBLE_CLOSE_SIZE = 32;
export const DISMISSIBLE_CLOSE_ICON_SIZE = 16;
export const DISMISSIBLE_CLOSE_ICON: IconName = 'close';
export const DISMISSIBLE_CLOSE_BACKGROUND = colors.onTint.control;
export const DISMISSIBLE_CLOSE_ICON_COLOR = colors.cerulean['800'];

export const dismissibleClosePropsSchema = z.object({
  accessibilityLabel: z.string().trim().min(1),
  /** Analytics action id for the press. */
  actionId: actionIdSchema.optional(),
  testID: z.string().optional(),
});

export const dismissiblePropsSchema = z.object({
  /** Stable identity of what was dismissed; the host store remembers it. */
  dismissKey: z.string().trim().min(1),
  /**
   * The container's own close control, drawn in its top-right corner over the
   * children. Absent means the container has no control of its own: another
   * container with the same `dismissKey` dismisses it.
   */
  close: dismissibleClosePropsSchema.optional(),
  /**
   * Announced to screen readers when the member dismisses the container, so
   * the content disappearing from under the focused control is conveyed
   * (ADR-025 Rule 4).
   */
  dismissedAnnouncement: z.string().trim().min(1).optional(),
  testID: z.string().optional(),
});

export type DismissibleProps = z.input<typeof dismissiblePropsSchema>;
export type DismissibleCloseProps = z.input<typeof dismissibleClosePropsSchema>;
